import fm from 'format-message';
import useNotifications from '@activebrands/core-web/hooks/useNotifications';
import RichText, { getRichTextAsString } from '@activebrands/core-web/libs/storyblok/RichText';
import type { RichTextData } from '@activebrands/core-web/types/components/richtext';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import { getFocusRingStyles } from '@activebrands/core-web/utils/style-functions/get-focus-styles';
// @ts-expect-error - Brand-level component resolved via build-time path alias
import StateIcon from 'components/icons/StateIcon';
// @ts-expect-error - Brand-level component resolved via build-time path alias
import NotificationWrapper from 'components/wrappers/NotificationWrapper';

type ErrorNotficationsProps = {
    id: string;
    label?: string;
    message?: RichTextData;
};

const visuallyHiddenStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0',
} as const;

const ErrorNotification = ({
    id,
    label = fm('Attention!'),
    message = fm('Something went wrong...'),
}: ErrorNotficationsProps) => {
    const [css] = useStyletron();
    const notifications = useNotifications();
    const messageText = typeof message === 'string' ? message : getRichTextAsString(message);

    return (
        <NotificationWrapper role="alert" aria-live="assertive" aria-atomic="true">
            <span style={visuallyHiddenStyle}>{label}: {messageText}</span>
            <div
                className={css({
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    alignContent: 'center',
                    padding: '16px 16px 8px',
                    color: 'var(--color-text-error)',
                })}
            >
                <span aria-hidden="true">{label}</span>
                <button
                    type="button"
                    aria-label={fm('Close')}
                    className={css({ cursor: 'pointer', ...getFocusRingStyles() })}
                    onClick={() => notifications.remove(id)}
                >
                    <StateIcon size="12px" state="close" />
                </button>
            </div>
            <div aria-hidden="true" className={css({ padding: '0px 16px 16px', color: 'var(--color-text)' })}>
                <RichText data={message} />
            </div>
        </NotificationWrapper>
    );
};

export default ErrorNotification;
